<template>
  <div
    :class="[
      'base-grid__ratio',
      { 'base-grid__ratio--active': isActive },
      { 'base-grid__ratio--inactive': !isActive },
    ]"
    @click="onClickRatio({ name, type })"
  >
    <div class="ratio-wrapper">
      <div class="ratio-bar">
        <span
          class="ratio-bar__thumb"
          :style="{
            width: `${computedWidth}%`,
          }"
        />
        <span
          v-if="computedHatchWidth > 0"
          class="ratio-bar__hatch"
          :style="{
            width: `${computedHatchWidth}%`,
          }"
        />
      </div>
      <span class="ratio-text ratio-text__name">
        {{ type ? `${type} / ${name}` : name }}
      </span>
      <span
        class="ratio-text"
        :style="{ right: name ? '30px' : 'auto' }"
      >
        <template v-if="props.displayText">
          {{ displayText }}
        </template>
        <template v-else>
          {{ hasUnit ? computedRatio + '%' : computedRatio }}
        </template>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { roundToDigit, roundToDigitNumber } from '@/common/utils/commonUtils';

interface Props {
  ratio?: number | null;
  name?: string;
  hasUnit?: boolean;
  type?: string;
  isActive?: boolean;
  displayText?: string;
  digit?: number;
  hatchedRatio?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  ratio: null,
  name: '',
  hasUnit: false,
  type: '',
  isActive: false,
  displayText: '',
  digit: 3,
  hatchedRatio: null,
});

interface Value {
  name: string;
  type: string;
}

interface Emit {
  (e: 'click-ratio', value: Value): void;
}
const emit = defineEmits<Emit>();

const computedRatio = computed(() =>
  props.ratio === null ? null : roundToDigit(props.ratio, props.digit),
);
const computedWidth = computed(() => {
  if (props.ratio === null) {
    return 0;
  }

  const width = roundToDigitNumber(props.ratio, props.digit);
  if (width > 100) {
    return 100;
  }

  return width;
});
const computedHatchWidth = computed(() => {
  if (props.hatchedRatio === null || props.hatchedRatio <= 0) {
    return 0;
  }

  // ratio(thumb)와 hatch는 분모가 다른 비율일 수 있어 합이 100을 넘을 수 있다 — 겹치지 않게 남은 폭으로 상한
  const maxWidth = 100 - computedWidth.value;
  const width = roundToDigitNumber(props.hatchedRatio, props.digit);
  if (width > maxWidth) {
    return maxWidth;
  }

  return width;
});

const onClickRatio = ({ name, type }) => {
  emit('click-ratio', { name, type });
};
</script>
